import React from 'react';
import { ArrowRight, Play, Building2, Users, Sparkles } from 'lucide-react';

const HERO_IMG = 'https://customer-assets.emergentagent.com/job_80a9eaca-abc5-4772-88de-8f55ddf92961/artifacts/ei52k4an_Screenshot%202026-07-14%20at%204.51.27%E2%80%AFPM.png';

/**
 * V2B — The Human Storyteller
 * Warm cream, serif headline, isometric illustration, playful & vertical-specific.
 */
export default function LandingV2B({ onGoToLogin, onGoToSignup }) {
  return (
    <div className="min-h-screen" style={{ background: '#FFFDF8', fontFamily: 'DM Sans, system-ui, sans-serif', color: '#1F2937' }}>
      {/* Nav */}
      <nav className="max-w-7xl mx-auto px-6 pt-8 flex items-center justify-between" data-testid="v2b-nav">
        <div className="flex items-center gap-2.5">
          <svg width="30" height="30" viewBox="0 0 30 30" fill="none">
            <path d="M15 3C15 3 6 13 6 20a9 9 0 0018 0c0-7-9-17-9-17z" fill="#6D28D9" />
          </svg>
          <span className="text-xl font-bold" style={{ fontFamily: 'Playfair Display', fontWeight: 700 }}>LodgiFlow</span>
        </div>
        <div className="hidden md:flex items-center gap-9 text-sm text-gray-600">
          <a href="#platform" className="hover:text-gray-900 transition-colors">Platform</a>
          <a href="#customers" className="hover:text-gray-900 transition-colors">Customers</a>
          <a href="#story" className="hover:text-gray-900 transition-colors">Our Story</a>
        </div>
        <div className="flex items-center gap-3">
          <button onClick={onGoToLogin} className="text-sm text-gray-700 hover:text-gray-900" data-testid="v2b-login">Log In</button>
          <button onClick={onGoToSignup} className="px-5 py-2.5 rounded-full text-sm font-semibold text-white transition-transform hover:scale-105" style={{ background: '#6D28D9' }} data-testid="v2b-cta-nav">Start Free</button>
        </div>
      </nav>

      {/* Hero */}
      <section className="max-w-7xl mx-auto px-6 pt-16 pb-20 grid lg:grid-cols-[1fr_1.15fr] gap-16 items-center">
        <div>
          <div className="inline-flex items-center gap-2 mb-6">
            <span className="h-px w-8 bg-purple-600" />
            <span className="text-xs font-bold uppercase tracking-widest text-purple-700">For hospitality technology teams</span>
          </div>
          <h1 style={{ fontFamily: 'Playfair Display', fontWeight: 600, fontSize: 'clamp(2.5rem, 5.5vw, 4.25rem)', lineHeight: 1.05, letterSpacing: '-0.02em' }} data-testid="v2b-headline">
            Built for the teams<br />
            that keep hotels <em style={{ color: '#6D28D9', fontStyle: 'italic' }}>running.</em>
          </h1>
          <p className="mt-6 text-lg text-gray-600 leading-relaxed max-w-lg">
            From 5 properties to 500 — LodgiFlow scales your technology governance without scaling your headcount. Made by hoteliers, for hoteliers.
          </p>
          <div className="mt-9 flex flex-wrap items-center gap-3">
            <button onClick={onGoToSignup} className="group inline-flex items-center gap-2 px-7 py-3.5 rounded-full text-sm font-bold text-white transition-transform hover:scale-[1.03]" style={{ background: '#6D28D9', boxShadow: '0 10px 30px -10px rgba(109,40,217,0.5)' }} data-testid="v2b-cta-primary">
              Get Started Free
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
            </button>
            <button onClick={onGoToLogin} className="group inline-flex items-center gap-2 px-6 py-3.5 rounded-full text-sm font-semibold border-2 border-gray-900 hover:bg-gray-900 hover:text-white transition-colors" data-testid="v2b-cta-secondary">
              <Play className="h-3.5 w-3.5 fill-current" />
              See How It Works
            </button>
          </div>

          {/* Trust micro-row */}
          <div className="mt-10 flex flex-wrap items-center gap-6">
            <div className="flex -space-x-2">
              {['#fbbf24','#f472b6','#60a5fa','#34d399'].map((c,i)=>(
                <div key={i} className="h-8 w-8 rounded-full border-2 border-white flex items-center justify-center text-white text-xs font-bold" style={{ background: c }}>
                  {['SB','MR','JT','AK'][i]}
                </div>
              ))}
            </div>
            <div className="text-xs text-gray-600">
              <div className="font-semibold text-gray-900">Loved by 30+ hotel operators</div>
              <div className="flex items-center gap-1">
                {'★★★★★'.split('').map((s,i)=>(<span key={i} className="text-yellow-500">★</span>))}
                <span className="ml-1">4.9/5 on Capterra</span>
              </div>
            </div>
          </div>
        </div>

        {/* Right — isometric illustration */}
        <div className="relative">
          <img src={HERO_IMG} alt="Hotel tech ecosystem" className="w-full h-auto animate-[floatY_5s_ease-in-out_infinite]" />
        </div>
      </section>

      {/* Scale journey fold */}
      <section className="border-t border-gray-200/60" style={{ background: '#FCF9F3' }}>
        <div className="max-w-6xl mx-auto px-6 py-20" data-testid="v2b-scale">
          <div className="text-center mb-14">
            <div className="text-xs font-bold uppercase tracking-widest text-purple-700 mb-3">The Scale Journey</div>
            <h2 style={{ fontFamily: 'Playfair Display', fontWeight: 600, fontSize: 'clamp(1.75rem,3.5vw,2.75rem)' }}>
              From <em className="italic text-purple-700">five</em> properties to <em className="italic text-purple-700">five hundred</em>.
            </h2>
            <p className="mt-3 text-gray-600 max-w-xl mx-auto">Same team. Same clarity. No new headcount required.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { size: '5–30', icon: Building2, title: 'Growing collections', desc: 'Get out of spreadsheets. Every property mapped in a week.', tint: '#EDE9FE' },
              { size: '30–100', icon: Users, title: 'Multi-brand portfolios', desc: 'Brand standards, compliance, and governance across regions.', tint: '#FEE7F5' },
              { size: '100+', icon: Sparkles, title: 'Enterprise operators', desc: 'AI-assisted decisions, custom KPIs, contract intelligence.', tint: '#FEF3C7' },
            ].map((it) => {
              const Icon = it.icon;
              return (
                <div key={it.size} className="relative bg-white rounded-3xl p-7 border border-gray-100 transition-all hover:-translate-y-1 hover:shadow-xl" style={{ boxShadow: '0 4px 20px -8px rgba(0,0,0,0.08)' }}>
                  <div className="inline-flex items-center justify-center h-11 w-11 rounded-2xl mb-4" style={{ background: it.tint }}>
                    <Icon className="h-5 w-5 text-purple-700" />
                  </div>
                  <div className="text-3xl font-bold mb-1" style={{ fontFamily: 'Playfair Display' }}>{it.size} <span className="text-sm text-gray-500 font-normal">properties</span></div>
                  <div className="font-semibold mt-2">{it.title}</div>
                  <div className="text-sm text-gray-600 mt-1.5 leading-relaxed">{it.desc}</div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <style>{`
        @keyframes floatY { 0%,100% { transform: translateY(0); } 50% { transform: translateY(-10px); } }
      `}</style>
    </div>
  );
}
